import type { EvaluationCriteriaSelectedItem, EvaluationCriterion } from 'app/types/evaluationCriteria';
import ButtonBase from 'core/modules/atoms/Button';
import SelectCustomizableBase from 'core/modules/atoms/SelectCustomizable/SelectCustomizable';
import DialogDefaultBase from 'core/modules/components/dialogs/components/Dialog/Dialog';
import EducationYearSelectorBase from 'core/modules/components/selects/EducationYearSelector/EducationYearSelector';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Icon, InputCheckbox, Tag as TagBase } from 'ui';
import LoadingDotsBase from 'ui/atoms/LoadingDots/LoadingDots';
import useSelectLearningObjectivesTanAI from './useSelectLearningObjectivesTanAI';

// Legacy JavaScript components without TypeScript definitions. Typed loosely (via `unknown`) to allow their dynamic props.
type LegacyComponent = React.ComponentType<Record<string, unknown>>;

const Button = ButtonBase as LegacyComponent;
const LoadingDots = LoadingDotsBase as LegacyComponent;
const SelectCustomizable = SelectCustomizableBase as LegacyComponent;
const Tag = TagBase as unknown as LegacyComponent;
const DialogDefault = DialogDefaultBase as LegacyComponent;
const EducationYearSelector = EducationYearSelectorBase as LegacyComponent;

type Props = Readonly<{
  disciplineId?: string;
  educationLevel?: string;
  educationYear?: string;
  onChange: (next: EvaluationCriteriaSelectedItem[]) => void;
  placeholder?: string;
  value: Array<EvaluationCriteriaSelectedItem | string>;
}>;

type CriterionTagProps = Readonly<{
  onRemove: (id: string) => void;
  tag: EvaluationCriteriaSelectedItem;
}>;

const TAG_TEXT_MAX_LENGTH = 20;

function CriterionTag({ onRemove, tag }: CriterionTagProps): JSX.Element {
  const fullText = tag?.description ?? '';
  const isTruncated = fullText.length > TAG_TEXT_MAX_LENGTH;
  const displayedText = isTruncated ? `${fullText.slice(0, TAG_TEXT_MAX_LENGTH)}…` : fullText;

  function handleClick(event: React.MouseEvent<HTMLDivElement>): void {
    event.stopPropagation();
    onRemove(tag?.id);
  }

  return (
    <li className="select-learning-objectives-tan-ai__tag-item">
      <Tag label={<span>{displayedText}</span>} onClick={handleClick} rightIcon="close" tooltip={isTruncated ? fullText : undefined} />
    </li>
  );
}

type CriterionRowProps = Readonly<{
  criterion: EvaluationCriterion;
  isSelected: boolean;
  onToggle: (id: string) => void;
  showLessLabel: string;
  showMoreLabel: string;
}>;

function noop(): void {}

function CriterionRow({ criterion, isSelected, onToggle, showLessLabel, showMoreLabel }: CriterionRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isTruncatable, setIsTruncatable] = useState<boolean>(false);
  const textRef = useRef<HTMLDivElement | null>(null);
  const description = criterion?.description ?? '';

  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return;
    setIsTruncatable(node.scrollWidth > node.clientWidth + 1);
  }, [description]);

  function handleRowClick(): void {
    onToggle(criterion.id);
  }

  function handleRowKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onToggle(criterion.id);
  }

  function handleToggleExpand(event: React.MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  }

  const textClassName = `select-learning-objectives-tan-ai__criterion-text${isExpanded ? ' select-learning-objectives-tan-ai__criterion-text--expanded' : ''}`;

  return (
    <li className={`select-learning-objectives-tan-ai__criterion${isSelected ? ' select-learning-objectives-tan-ai__criterion--selected' : ''}`}>
      {/* role="button" is required: a native <button> can't wrap the InputCheckbox's <input> nor the nested Ver más <button> */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="select-learning-objectives-tan-ai__criterion-label" onClick={handleRowClick} onKeyDown={handleRowKeyDown} role="button" tabIndex={0}>
        <InputCheckbox checked={isSelected} onChange={noop} />
        <div className="select-learning-objectives-tan-ai__criterion-content">
          <div className={textClassName} ref={textRef}>
            {description}
          </div>
          {(isTruncatable || isExpanded) && (
            <button className="select-learning-objectives-tan-ai__criterion-toggle" onClick={handleToggleExpand} type="button">
              {isExpanded ? showLessLabel : showMoreLabel}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function SelectLearningObjectivesTanAI({ disciplineId, educationLevel, educationYear, onChange, placeholder, value = [] }: Props): JSX.Element {
  const {
    t,
    isOpen,
    isLoadingRegions,
    isLoadingCriteria,
    hasSearched,
    isSearchEnabled,
    regionOptions,
    selectedRegionOption,
    disciplineOptions,
    selectedDisciplineOption,
    criteria,
    normalizedValue,
    pendingSelection,
    resolvedPlaceholder,
    educationYearValue,
    showMoreLabel,
    showLessLabel,
    handleOpenDialog,
    handleInputKeyDown,
    handleRegionChange,
    handleDisciplineChange,
    handleEducationYearChange,
    handleSearch,
    handleToggleById,
    handleRemoveTag,
    handleSave,
    handleCancel,
    isCriterionSelected,
  } = useSelectLearningObjectivesTanAI({ disciplineId, educationLevel, educationYear, onChange, placeholder, value });

  return (
    <>
      <button className="select-learning-objectives-tan-ai__input-container " onClick={handleOpenDialog} onKeyDown={handleInputKeyDown} type="button">
        <div className="select-learning-objectives-tan-ai__tags">
          {normalizedValue.length > 0 ? (
            <ul className="select-learning-objectives-tan-ai__tag-list">
              {normalizedValue.map((item) => (
                <CriterionTag key={item?.id} onRemove={handleRemoveTag} tag={item} />
              ))}
            </ul>
          ) : (
            <span className="select-learning-objectives-tan-ai__placeholder">{resolvedPlaceholder}</span>
          )}
        </div>
        <div className="select-learning-objectives-tan-ai__chevron">
          <Icon color="grey-01" name="chevron-down" size="sm" />
        </div>
      </button>

      <DialogDefault
        buttonTextCancel={t('actions:Cancel')}
        buttonTextSucess={t('actions:Save')}
        onCancel={handleCancel}
        onClose={handleCancel}
        onSuccess={handleSave}
        open={isOpen}
        showHeader={true}
        size="xl"
        title={t('chatbot:teacher_tools_select_evaluation_criteria_title')}
      >
        <div className="select-learning-objectives-tan-ai__dialog">
          <div className="select-learning-objectives-tan-ai__filters">
            <div className="select-learning-objectives-tan-ai__filter-field">
              <SelectCustomizable
                isSearchable={true}
                label={t('chatbot:teacher_tools_select_evaluation_criteria_region_label')}
                onChange={handleRegionChange}
                options={regionOptions}
                placeholder={
                  isLoadingRegions
                    ? t('chatbot:teacher_tools_select_evaluation_criteria_loading_regions')
                    : t('chatbot:teacher_tools_select_evaluation_criteria_region_placeholder')
                }
                value={selectedRegionOption}
              />
            </div>

            <div className="select-learning-objectives-tan-ai__filter-field">
              <SelectCustomizable
                isSearchable={true}
                label={t('chatbot:teacher_tools_select_evaluation_criteria_discipline_label')}
                onChange={handleDisciplineChange}
                options={disciplineOptions}
                placeholder={t('chatbot:teacher_tools_select_evaluation_criteria_discipline_placeholder')}
                value={selectedDisciplineOption}
              />
            </div>

            <div className="select-learning-objectives-tan-ai__filter-field">
              <EducationYearSelector onChange={handleEducationYearChange} showLabel={true} value={educationYearValue} />
            </div>

            <div className="select-learning-objectives-tan-ai__search-button">
              <Button
                color="primary"
                disabled={!isSearchEnabled || isLoadingCriteria}
                onClick={handleSearch}
                text={t('chatbot:teacher_tools_select_evaluation_criteria_search_button')}
              />
            </div>
          </div>

          <div className="select-learning-objectives-tan-ai__results">
            {isLoadingCriteria && (
              <div className="select-learning-objectives-tan-ai__loading">
                <LoadingDots />
                <span>{t('chatbot:teacher_tools_select_evaluation_criteria_loading_criteria')}</span>
              </div>
            )}

            {!isLoadingCriteria && !hasSearched && (
              <p className="select-learning-objectives-tan-ai__empty">{t('chatbot:teacher_tools_select_evaluation_criteria_empty_filters_state')}</p>
            )}

            {!isLoadingCriteria && hasSearched && criteria.length === 0 && (
              <p className="select-learning-objectives-tan-ai__empty">{t('chatbot:teacher_tools_select_evaluation_criteria_no_results_state')}</p>
            )}

            {!isLoadingCriteria && criteria.length > 0 && (
              <>
                <p className="select-learning-objectives-tan-ai__count">
                  {t('chatbot:teacher_tools_select_evaluation_criteria_count_label', { count: pendingSelection.length })}
                </p>
                <ul className="select-learning-objectives-tan-ai__criteria-list">
                  {criteria.map((criterion) => (
                    <CriterionRow
                      criterion={criterion}
                      isSelected={isCriterionSelected(criterion.id)}
                      key={criterion.id}
                      onToggle={handleToggleById}
                      showLessLabel={showLessLabel}
                      showMoreLabel={showMoreLabel}
                    />
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </DialogDefault>
    </>
  );
}

export default SelectLearningObjectivesTanAI;
